import { mediaQueryLarge, requestIdleCallback } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  /** DCR: the card image width the grid's sizes attributes describe. */
  #imageWidth = 0;

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#changeLayout(target.value);
  }

  /**
   * Sets the layout, at once.
   *
   * DCR: this used to run inside a view transition. That waits for the browser to
   * go idle (to work out which cards to animate), then crossfades the swap, so a
   * tap on "1" or "8" answered a beat late and then faded. The columns are a
   * choice the shopper just made and should change on the tap.
   *
   * @param {string} value
   */
  #changeLayout = (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    const cardWidthBefore = this.#cardImageWidth();
    this.#setLayout(value);
    this.#raiseImageSizes(cardWidthBefore);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * DCR: the width of a card image as laid out now, in CSS pixels.
   * @returns {number}
   */
  #cardImageWidth() {
    const image = this.refs.grid?.querySelector('.card-gallery img');
    return image ? Math.ceil(image.getBoundingClientRect().width) : 0;
  }

  /**
   * DCR: card images carry a sizes attribute for the default view (see
   * snippets/card-gallery.liquid). When the shopper picks fewer, larger columns,
   * rewrite it to the new card width so the browser fetches a sharper file.
   * Only ever raised: images already fetched for a larger card are good enough
   * for a smaller one, and lowering sizes would make the browser download a
   * smaller copy of every image it already has.
   *
   * @param {number} widthBefore - the card image width before the layout changed
   */
  #raiseImageSizes(widthBefore) {
    const { grid } = this.refs;
    if (!grid) return;

    // The first change records the width the page loaded at, which the
    // server-rendered sizes already covers.
    this.#imageWidth ||= widthBefore;

    const width = this.#cardImageWidth();
    if (!width || width <= this.#imageWidth) return;

    this.#imageWidth = width;
    // Read by product-card.js, for cards that infinite scroll adds later.
    grid.dataset.dcrImageWidth = String(width);
    for (const image of grid.querySelectorAll('.card-gallery img')) {
      const lazy = image.getAttribute('loading') === 'lazy';
      image.setAttribute('sizes', `${lazy ? 'auto, ' : ''}${width}px`);
    }
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
